import { useEffect, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export const RESEND_COOLDOWN_S = 60;

interface Props {
  email: string;
  busy: boolean;
  error: string | null;           // i18n key
  onSubmit: (otp: string) => void;
  onResend: () => Promise<void>;
  onChangeEmail: () => void;
  styles: Record<string, CSSProperties>;
}

/** Step 2 of /join: type the 6-digit code we mailed. Resend has a cooldown. */
export default function JoinOtpStep({ email, busy, error, onSubmit, onResend, onChangeEmail, styles: S }: Props) {
  const { t } = useTranslation();
  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_S);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(otp.replace(/\s+/g, '')); };
  const resend = async () => { setResent(false); await onResend(); setResent(true); setCooldown(RESEND_COOLDOWN_S); };

  return (
    <form onSubmit={submit} noValidate>
      <h1 style={S.title}>{t('join.otpTitle')}</h1>
      <p style={S.muted}>{t('join.otpSent', { email })}</p>
      <label style={S.field}>
        <span style={S.label}>{t('join.otpLabel')}</span>
        <input
          type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]*" maxLength={7}
          value={otp} onChange={(e) => setOtp(e.target.value)} autoFocus
          style={{ ...S.input, letterSpacing: '0.3em', fontSize: '22px', textAlign: 'center', borderColor: error ? '#f87171' : 'var(--color-border)' }}
        />
        <span style={S.small}>{t('join.otpHint')}</span>
      </label>
      {error && <p style={S.error}>{t(error)}</p>}
      {resent && !error && <p style={{ ...S.small, marginBottom: '12px' }}>{t('join.otpResent')}</p>}
      <button type="submit" className="btn btn-glow" style={S.button} disabled={busy || otp.replace(/\s+/g, '').length !== 6}>
        {busy ? t('join.submitting') : t('join.submit')}
      </button>
      <p style={{ ...S.small, marginTop: '12px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <button type="button" onClick={resend} disabled={busy || cooldown > 0} style={S.linkButton}>
          {cooldown > 0 ? t('join.otpResendIn', { s: cooldown }) : t('join.otpResend')}
        </button>
        <button type="button" onClick={onChangeEmail} disabled={busy} style={S.linkButton}>{t('join.otpChangeEmail')}</button>
      </p>
    </form>
  );
}
